import {Injectable} from "@angular/core";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {DeliveryPointLocal} from "@app/core/delivery-point/domain/delivery-point.local";
import {NewDeliveryPointMapper} from "@app/core/delivery-point/infrastructure/mappers/new-delivery-point.mapper";
import {ApiUrls} from "@app/core/shared/infrastructure/api-urls";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {EuropastryBaseHandler} from "@app/core/shared/infrastructure/base.handler/europastry-base.handler";
import {Observable, retry, throwError} from "rxjs";
import {catchError, map, switchMap, take} from "rxjs/operators";
import {NewDeliveryPoint} from "../../delivery-point/domain/dtos/new-delivery-point";
import {CartRemote} from "../domain/cart.remote";
import {Cart} from "../domain/dtos/cart";
import {CheckoutAction} from "../domain/dtos/cart-action.enum";
import {CartMapper} from "./mappers/cart.mapper";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {getAdyenChannel} from "@app/core/shared/infrastructure/adyen-channel";
import {HttpErrorResponse} from "@angular/common/http";
import {InfrastructureError, InfrastructureErrorType} from "@app/core/shared/domain/infrastructure.error";
import {PromoterRepo} from "@app/core/promoter/domain/promoter-repo";
import {PaymentMethodType} from "@app/core/delivery-point/application/dtos/payment-method-type";


@Injectable({
    providedIn: 'root'
})
export class CartWebService implements CartRemote {

    private ADYEN_CHANNEL =  getAdyenChannel();

    constructor(
        private baseHandler: EuropastryBaseHandler,
        private deliveryPointLocal: DeliveryPointLocal,
        private credentialsStorage: CredentialsStorage,
        private promoterStorage: PromoterRepo,
        private apiUrls: ApiUrls,
    ) { }

    validateOrder(cart: Cart): Observable<Cart> {
        return this.execute(CheckoutAction.VALIDATE, cart, undefined, false);
    }

    finalizeOrder(cart: Cart, isDuplicated?:boolean): Observable<Cart> {
        return this.execute(CheckoutAction.CHECKOUT, cart, undefined, !!isDuplicated).pipe(
            // The variable error is a CartError or an Error.
            // If error is not a CartError, we return the error as it is.
            // CartError is a cart with the property errors.
          catchError(error => throwError(() => error.errors ?? error))
        );
    }

    finalizeOrderWithNewCustomer(cart: Cart, newCustomerData: NewDeliveryPoint,recaptchaToken: string, isDuplicated?:boolean): Observable<Cart> {
        return this.execute(CheckoutAction.CHECKOUT, cart, newCustomerData, !!isDuplicated, recaptchaToken);
    }

    getLastOrderId(): Observable<number> {
        // Generic error to avoid showing error details to the user when the request fails.
        const genericError = new Error('Something went wrong. Please try again later.');
        const deliveryPointCode = this.deliveryPointLocal.getDeliveryPointCode();
        const url = this.apiUrls.LAST_ORDER_ID + deliveryPointCode;
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(url)
            .authorization(this.credentialsStorage.getCredentials())
            .build();

        return this.baseHandler.execute(parameters).pipe(
            map((response: any) => response.results),
            map((orderId: (number | null)) => {
                // If the response is null, it means that no order id has been found.
                if (orderId == null) {
                    throw genericError;
                }
                return orderId;
            }),
            catchError(BaseHandleErrorMapper.handleError),
            catchError(error => {
                if (checkIsServiceUnavailableError(error)) {
                    // Return a generic error to avoid that the ui redirects to the service unavailable page.
                    return throwError(() => genericError);
                }
                return throwError(() => error);
            })
        )
    }

    /**
     * Executes a checkout action request for a cart.
     *
     * This method is used to process different checkout actions—such as validating an order,
     * finalizing an order, or finalizing an order with new customer data. It prepares and sends the appropriate
     * API request, ensuring that the necessary parameters (like duplicate check or recaptcha token) are included.
     *
     * @param {CheckoutAction} actionOrder - The checkout action to perform (e.g., VALIDATE or CHECKOUT).
     * @param {Cart} cart - The cart object containing order details.
     * @param {NewDeliveryPoint | undefined} newDeliveryPoint - Optional new customer data, used when finalizing an order with a new customer.
     * @param {boolean} duplicateCheck - Flag indicating whether to allow the order to proceed even if it is detected as a duplicate.
     * @param {string} [recaptchaToken] - Optional reCAPTCHA token used with new customer data.
     * @returns {Observable<Cart>} An observable emitting the updated cart.
     */
    private execute(actionOrder: CheckoutAction, cart: Cart, newDeliveryPoint: NewDeliveryPoint | undefined, duplicateCheck: boolean, recaptchaToken?: string): Observable<Cart> {

        const isUsualConditions = cart.paymentMethod === PaymentMethodType.USUAL_CONDITIONS;
        const isCard = cart.paymentMethod === PaymentMethodType.VISA;

        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(
            take(1),
            switchMap(deliveryPointCode => {

            // If the payment method is not UsualConditions or Card, we change it to visa to avoid problems with Backend.
            // Later on in this function, it will be changed so that the function returns the cart with the correct payment method.
            const modifiedCart: Cart = {
                ...cart,
                paymentMethod: (isUsualConditions || isCard) ? cart.paymentMethod : PaymentMethodType.VISA,
            }

            let requestBody: any = CartMapper.serialize(modifiedCart);
            requestBody['action'] = actionOrder.toString();
            requestBody['client'] = deliveryPointCode;

            const credentials = this.credentialsStorage.getCredentials();
            const promoter = this.promoterStorage.get();

            if (newDeliveryPoint) {
                requestBody['new_client'] = NewDeliveryPointMapper.serialize(newDeliveryPoint);
                requestBody['recaptchaToken'] = recaptchaToken;
            }

            if (promoter) {
                requestBody['promoter'] = promoter;
            }

            if (duplicateCheck) {
                requestBody['duplicateCheck'] = true;
            }

            const parameters = new HandlerBuilder()
                .method(HttpMethod.post)
                .url(this.apiUrls.CONFIRM_ORDER + deliveryPointCode)
                .body(requestBody)
                .authorization(credentials)
                .build();

            return this.baseHandler.execute(parameters).pipe(
                map((response: any) => {
                    return {
                        ...response,
                        // Restore the original payment method.
                        payment_method: cart.paymentMethod
                    }
                }),
                map(CartMapper.deserialize),
                catchError((error: HttpErrorResponse) => {
                    // Cart Error is a cart with errors.
                    const cartErrors = error.error?.errors;
                    const hasCartErrors = cartErrors != null;
                    if (hasCartErrors) {
                        return throwError(() => ({
                            ...CartMapper.deserialize(error.error),
                             // Restore the original payment method.
                            payment_method: cart.paymentMethod
                        }));
                    }

                    // If the error is not a cart error, we return the error as it is.
                    return BaseHandleErrorMapper.handleError(error).pipe(
                        catchError((e: Error) => {
                            if (actionOrder === CheckoutAction.CHECKOUT && checkIsServiceUnavailableError(e)) {
                                // If there is a service unavailable error in the checkout action,
                                // we get the last order id to avoid the user to make a duplicated order.
                                const maxRetries = 3;
                                const delayBetweenRetriesInMilliseconds = 2000;
                                return this.getLastOrderId().pipe(
                                    // If the request fails, we retry 3 times with a delay of 2 seconds between each attempt.
                                    retry({
                                        count: maxRetries,
                                        delay: delayBetweenRetriesInMilliseconds,
                                        resetOnSuccess: true,
                                    }),
                                    // If the request is successful, we return the cart with the last order id.
                                    map((lasOrderId: number) => {
                                        return {
                                            ...cart,
                                            id: lasOrderId
                                        }
                                })
                                );
                            }
                            return throwError(() => e);
                        })
                    );
                })
            );
        }));
    }

    getPaymentMethods(amountInMinorUnits: number): Observable<any> {
        return this.deliveryPointLocal.currentDeliveryPointCode$.pipe(
            take(1),
            switchMap(deliveryPointCode => {
                const parameters = new HandlerBuilder()
                    .method(HttpMethod.post)
                    .url(this.apiUrls.ADYEN_PAYMENT_METHODS + deliveryPointCode)
                    .authorization(this.credentialsStorage.getCredentials())
                    .header(new Map().set('channel', this.ADYEN_CHANNEL))
                    .body({
                        amountInMinorUnits: amountInMinorUnits,
                    })
                    .build();

                return this.baseHandler.execute(parameters).pipe(
                    catchError(BaseHandleErrorMapper.handleError),
                );
            }));

    }
}

function checkIsServiceUnavailableError(e: Error): boolean {
    const isInfrastructureError = e instanceof InfrastructureError;
    return isInfrastructureError && e.type === InfrastructureErrorType.ServiceUnavailableError;
}
